import React from "react";
import _ from "lodash";
import { Formik } from "formik";
import { MdKeyboardBackspace, MdEdit, MdCancel } from "react-icons/md";
import { IconButton } from "~/components/crud/styles";
import DataTable from "~/components/datatable";
import { InputLabel, Input } from "~/components/form";
import { formats } from "~/helper";
import useEventAccredit from "~/view-data/use-event-accredit";
import {
  AccreditContainer,
  FormHeader,
  Row,
  SearchContainer,
  TableContainer,
  Select,
} from "~/views/events/accredit/styles";
import AreaModal from "~/views/events/accredit/modal-area";

const statusMap = {
  pending: "Pendente",
  approved: "Aprovado",
  reproved: "Reprovado",
  replaced: "Substituído",
};

const getColumns = (
  status,
  areas,
  canAccredit,
  disableAreaSelect,
  onChangeArea
) => {
  const columns = [
    {
      name: "Colaborador",
      selector: "identify",
      format: (row) =>
        `${formats.cpf(_.get(row, "identify")) ||
          _.get(row, "identify") ||
          ""} - ${_.get(row, "name")}`,
    },
    {
      name: "Função",
      selector: "role",
      width: "200px",
      format: (row) => _.get(row, "role.name"),
    },
  ];

  if (/^(accreditation)$/.test(status)) {
    columns.push({
      name: "Telefone",
      selector: "phone",
      width: "150px",
      format: (row) => formats.phone(_.get(row, "phone")),
    });

    columns.push({
      name: "Email",
      selector: "email",
      format: (row) => _.get(row, "email"),
    });

    columns.push({
      name: "Acesso",
      selector: "area",
      width: "180px",
      cell: (row) => {
        let value = _.get(row, `checkInArea`) || "";

        if (!canAccredit) {
          return <div>{value || "Não relacionado"}</div>;
        }
        return (
          <Select
            disabled={disableAreaSelect}
            value={value}
            onChange={(evt) =>
              onChangeArea({ ...row, area: _.get(evt, "target.value") })
            }
          >
            <option value="">Não relacionado</option>
            {_.map(areas, (area, idx) => (
              <option key={idx} value={area}>
                {area}
              </option>
            ))}
          </Select>
        );
      },
    });
  }
  if (/^(checklist|running|finished)$/.test(status)) {
    columns.push({
      name: "Situação",
      selector: "status",
      format: (row) => statusMap[_.get(row, `status`)] || "",
    });
  }
  if (/^(running|finished)$/.test(status)) {
    columns.push({
      name: "Check-in",
      selector: "checkInDate",
      format: (row) =>
        formats.dateTimeZone(_.get(row, `checkInDate`), "dd/MM/yyyy HH:mm"),
    });
    columns.push({
      name: "Check-out",
      selector: "checkOutDate",
      format: (row) =>
        formats.dateTimeZone(_.get(row, `checkOutDate`), "dd/MM/yyyy HH:mm"),
    });
  }
  return columns;
};

const getFilterLabel = (rows, filter) => {
  if (!filter || (!filter.name && !filter.area)) {
    return "";
  }
  return ` - Filtrados (${_.size(rows)})`;
};

const getRows = (model, filter = {}) => {
  let list = _.get(model, "employees") || [];

  return _.filter(list, (r) => {
    return (
      (!filter.area ||
        filter.area === r.checkInArea ||
        (filter.area === "--" && !r.checkInArea)) &&
      (!filter.name || new RegExp(`^${filter.name}`, "i").test(r.name)) &&
      (!filter.role || String(r.role.id) === String(filter.role))
    );
  });
};

function Accredit({ acls, history }) {
  const canAccredit = acls.includes("C"),
    { state, actions } = useEventAccredit(history),
    model = _.get(state, "model") || {},
    isPress = _.get(model, "suppliers.isPress"),
    areas = isPress ? ["Imprensa"] : _.get(model, "place.areas") || [],
    roles = _.get(state, "roles") || [],
    hasSelection = !_.isEmpty(state.selecteds);

  let rows = getRows(model, state.filter);

  return (
    <AccreditContainer>
      <div className="contents">
        <div className="form-data">
          <Row span={2}>
            <InputLabel label="Evento" value={_.get(model, "name")} />

            <InputLabel label="Tipo" value={_.get(model, "eventType.name")} />
          </Row>

          <Row span={4}>
            <InputLabel label="Local" value={_.get(model, "place.name")} />

            <InputLabel
              label="Data início Credenciamento"
              value={formats.dateTimeZone(
                _.get(model, "accreditationStartDate"),
                "dd/MM/yyyy"
              )}
            />

            <InputLabel
              label="Data término Credenciamento"
              value={formats.dateTimeZone(
                _.get(model, "accreditationEndDate"),
                "dd/MM/yyyy"
              )}
            />

            <InputLabel
              label="Data do Evento"
              value={formats.dateTimeZone(
                _.get(model, "eventDate"),
                "dd/MM/yyyy"
              )}
            />
          </Row>
        </div>

        <TableContainer showForm={state.showForm}>
          <FormHeader>
            <span>
              Colaboradores ({_.size(model.employees)}){" "}
              {getFilterLabel(rows, state.filter)}
            </span>

            <Formik
              enableReinitialize={true}
              validateOnMount={true}
              initialValues={state.filter || { name: "", area: "", role: "" }}
            >
              {({ values, setFieldValue }) => (
                <SearchContainer>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Nome"
                    onChange={(evt) =>
                      actions.changeFilter({
                        ...values,
                        name: _.get(evt, "target.value"),
                      })
                    }
                  />
                  <Select
                    name="area"
                    value={values.area}
                    onChange={(evt) =>
                      actions.changeFilter({
                        ...values,
                        area: _.get(evt, "target.value"),
                      })
                    }
                  >
                    <option value="">Todas áreas</option>
                    <option value="--">Não relacionados</option>
                    {_.map(areas, (area, idx) => (
                      <option key={idx} value={area}>
                        {area}
                      </option>
                    ))}
                  </Select>

                  <Select
                    name="role"
                    value={values.role}
                    onChange={(evt) =>
                      actions.changeFilter({
                        ...values,
                        role: _.get(evt, "target.value"),
                      })
                    }
                  >
                    <option value="">Todas funções</option>
                    {_.map(roles, (role, idx) => (
                      <option key={`role-${idx}`} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Select>
                  <IconButton
                    title="Limpar"
                    size={28}
                    onClick={() => actions.changeFilter({ name: "", area: "", role: "" })}
                  >
                    <MdCancel />
                  </IconButton>
                </SearchContainer>
              )}
            </Formik>
          </FormHeader>

          <div className="list">
            <DataTable
              columns={getColumns(
                _.get(model, "status"),
                areas,
                canAccredit,
                hasSelection,
                actions.changeArea
              )}
              data={{ rows }}
              noPagination={true}
              emptyText="Nenhum colaborador credenciado"
              onRowClicked={_.noop}
              extraOptions={{
                ignoreRowClicked: true,
                selectableRows: /^(accreditation)$/.test(
                  _.get(model, "status")
                ),
                selectableRowsHighlight: false,
                selectableRowSelected: (row) => {
                  return (state.selecteds || []).includes(row.id);
                },
                onSelectedRowsChange: ({
                  allSelected,
                  selectedCount,
                  selectedRows,
                }) => {
                  actions.selectPersons(selectedRows);
                },
              }}
            />
          </div>
        </TableContainer>
      </div>

      <div className="toolbar">
        <IconButton title="Cancelar" onClick={() => history.push("/events")}>
          <MdKeyboardBackspace />
        </IconButton>

        {hasSelection && (
          <IconButton
            title="Definir Acesso"
            onClick={() => actions.openAreaModal({})}
          >
            <MdEdit />
          </IconButton>
        )}
      </div>

      <AreaModal
        loading={state.modalLoading}
        data={state.modalData || {}}
        areas={areas}
        handleOnSubmit={(value) => actions.changeArea(value, state.selecteds)}
        isOpen={state.showAreaModal}
        closeModal={actions.closeAreaModal}
        title={
          <>Definir acesso de ({_.size(state.selecteds)}) colaborador(es)</>
        }
      />
    </AccreditContainer>
  );
}

export default Accredit;
